import {
    Typography, Card, CardContent, Box, Stack, Chip, CircularProgress, Alert,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PlaceIcon from '@mui/icons-material/Place';
import ReviewsIcon from '@mui/icons-material/Reviews';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import RateReviewIcon from '@mui/icons-material/RateReview';
import GavelIcon from '@mui/icons-material/Gavel';
import PaymentsIcon from '@mui/icons-material/Payments';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';


type StatsOverview = {
    usersTotal: number;
    usersNewLast30: number;
    placesTotal: number;
    placesActive: number;
    placesPendingModeration: number;
    reviewsTotal: number;
    reviewsPendingText: number;
    externalReviewsTotal: number;
    externalReviewsPending: number;
    reviewPhotosPending: number;
    externalReviewPhotosPending: number;
    paymentsLast30Count: number;
    paymentsLast30Amount: number;
};


/**
 * Kartītes tips - metrics rādītājs ar galveno skaitli, atšķirīgu apakšlīniju un
 * saiti uz attiecīgo sadaļu. Warning chip parādās, kad ir kaut kas moderācijai.
 */
type MetricCardProps = {
    icon: React.ReactNode;
    label: string;
    value: number | string;
    subtitle?: string;
    warning?: { count: number; label: string };
    to?: string;
    color?: string;
};

function MetricCard({ icon, label, value, subtitle, warning, to, color }: MetricCardProps) {
    const inner = (
        <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                <Box sx={{ color: color ?? 'primary.main', fontSize: 28, display: 'flex' }}>
                    {icon}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {label}
                </Typography>
                {warning && warning.count > 0 && (
                    <Chip size="small" color="warning" label={`${warning.count} ${warning.label}`} />
                )}
            </Stack>
            <Typography variant="h4" sx={{ mb: 0.5 }}>{value}</Typography>
            {subtitle && (
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            )}
        </CardContent>
    );

    if (!to) return <Card>{inner}</Card>;
    return (
        <Card
            component={RouterLink}
            to={to}
            sx={{
                textDecoration: 'none',
                display: 'block',
                transition: 'transform .15s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
            }}
        >
            {inner}
        </Card>
    );
}


export function DashboardPage() {
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-stats-overview'],
        queryFn: async () => {
            const resp = await api.get<StatsOverview>('/api/v1/admin/stats/overview');
            return resp.data;
        },
        staleTime: 60_000,
    });

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Pārskats</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Sistēmas galvenie rādītāji un moderācijas rindas. Klikšķini uz kartītes, lai atvērtu sadaļu.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt statistiku: {String((error as Error).message)}
                </Alert>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {data && (
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                            lg: 'repeat(4, 1fr)',
                        },
                    }}
                >
                    <MetricCard
                        icon={<PeopleIcon fontSize="inherit" />}
                        label="Lietotāji"
                        value={data.usersTotal}
                        subtitle={`+${data.usersNewLast30} pēdējās 30 dienās`}
                        to="/users"
                    />
                    <MetricCard
                        icon={<PlaceIcon fontSize="inherit" />}
                        label="Vietas"
                        value={data.placesTotal}
                        subtitle={`${data.placesActive} aktīvas`}
                        warning={{ count: data.placesPendingModeration, label: 'gaida' }}
                        to="/places"
                    />
                    <MetricCard
                        icon={<ReviewsIcon fontSize="inherit" />}
                        label="Atsauksmes"
                        value={data.reviewsTotal}
                        subtitle="Unicnet vietām"
                        warning={{ count: data.reviewsPendingText, label: 'gaida' }}
                        to="/reviews"
                    />
                    <MetricCard
                        icon={<RateReviewIcon fontSize="inherit" />}
                        label="Ārējās atsauksmes"
                        value={data.externalReviewsTotal}
                        subtitle="Google/OSM/Refugee/Foursquare"
                        warning={{ count: data.externalReviewsPending, label: 'gaida' }}
                        to="/moderation/external-reviews"
                    />
                    <MetricCard
                        icon={<PhotoLibraryIcon fontSize="inherit" />}
                        label="Foto moderācija"
                        value={data.reviewPhotosPending}
                        subtitle="Pending review-foto"
                        color={data.reviewPhotosPending > 0 ? 'warning.main' : undefined}
                        to="/photos"
                    />
                    <MetricCard
                        icon={<PhotoLibraryIcon fontSize="inherit" />}
                        label="Ārējo foto moderācija"
                        value={data.externalReviewPhotosPending}
                        subtitle="Pending ext review-foto"
                        color={data.externalReviewPhotosPending > 0 ? 'warning.main' : undefined}
                        to="/moderation/external-review-photos"
                    />
                    <MetricCard
                        icon={<GavelIcon fontSize="inherit" />}
                        label="Vietu moderācija"
                        value={data.placesPendingModeration}
                        subtitle="Pending SP ielikšana"
                        color={data.placesPendingModeration > 0 ? 'warning.main' : undefined}
                        to="/moderation/places"
                    />
                    <MetricCard
                        icon={<PaymentsIcon fontSize="inherit" />}
                        label="Maksājumi (30d)"
                        value={data.paymentsLast30Amount.toFixed(2) + ' EUR'}
                        subtitle={`${data.paymentsLast30Count} maksājums(-i)`}
                        to="/payments"
                    />
                </Box>
            )}
        </Box>
    );
}
